/**
 * Analytics service — thin wrapper around the GTM dataLayer.
 *
 * GTM container GTM-N9Q5LS54 is initialized only after cookie consent is
 * granted (see services/cookieConsent.ts, called from index.tsx and from
 * CookieConsentBanner.tsx's onAccept). All helpers below push structured
 * events to window.dataLayer, which GTM forwards to GA4 — pushes made
 * before consent are harmless no-ops since grantAnalyticsConsent() resets
 * window.dataLayer right before GTM loads, so nothing pre-consent is ever
 * retroactively forwarded.
 *
 * sessionStorage key used for CTA→purchase correlation: "last_cta_feature"
 */

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

function pushEvent(event: string, params: Record<string, unknown> = {}): void {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...params });
  } catch {
    // Never let analytics errors affect the user flow
  }
}

// ─── User identity ───────────────────────────────────────────────────────────

/**
 * Pushes the authenticated user's ID into the GTM dataLayer so GA4 can
 * deduplicate sessions across web and mobile for the same account.
 *
 * Call immediately after a successful login or silent token refresh.
 * The GTM container must map the `user_id` dataLayer variable to the
 * GA4 Configuration tag (Fields to Set → user_id).
 */
export function setAnalyticsUserId(userId: string): void {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ user_id: userId });
  } catch {
    // Never let analytics errors affect the user flow
  }
}

/**
 * Clears the user ID from the GTM dataLayer on logout so that subsequent
 * anonymous events are not attributed to the previous user.
 */
export function clearAnalyticsUserId(): void {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ user_id: undefined });
  } catch {
    // Never let analytics errors affect the user flow
  }
}

// ─── CTA: SubscriptionUpgradePrompt ─────────────────────────────────────────

/**
 * Fired when the SubscriptionUpgradePrompt modal becomes visible.
 * Also writes `cta_feature` to sessionStorage for later purchase correlation.
 *
 * @param ctaTrigger  Machine-readable reason the CTA opened, e.g.
 *   "premium_event_required" | "guest_limit" | "monthly_limit_reached" |
 *   "view_attendees" | "view_chat_members" | "create_paid_event"
 */
export function trackCtaShown(ctaFeature: string, ctaTitle: string, ctaTrigger?: string, extraParams?: Record<string, unknown>): void {
  try {
    sessionStorage.setItem('last_cta_feature', ctaFeature);
  } catch {
    // sessionStorage not available (private mode edge case)
  }
  pushEvent('subscription_cta_shown', {
    cta_feature: ctaFeature,
    cta_title: ctaTitle,
    cta_location: typeof window !== 'undefined' ? window.location.pathname : '',
    ...(ctaTrigger ? { cta_trigger: ctaTrigger } : {}),
    ...(extraParams ?? {}),
  });
}

/**
 * Fired when the user dismisses the CTA without proceeding (X button or
 * "Continua senza Premium" button).
 */
export function trackCtaDismissed(ctaFeature: string): void {
  pushEvent('subscription_cta_dismissed', {
    cta_feature: ctaFeature,
  });
}

/**
 * Fired when the user clicks "Scopri i piani Premium" inside the CTA.
 */
export function trackCtaPlansClicked(ctaFeature: string): void {
  pushEvent('subscription_cta_plans_clicked', {
    cta_feature: ctaFeature,
  });
}

// ─── CTA: ProfilePhotoNudgePrompt ────────────────────────────────────────────

/**
 * Fired when the "add a real profile photo" nudge becomes visible.
 * Also writes `cta_feature` to sessionStorage for later completion correlation.
 *
 * @param touchpoint  Where the nudge was shown, e.g. "event_subscribe" |
 *   "first_login" | "first_chat_message" | "banner"
 */
export function trackProfilePhotoNudgeShown(touchpoint: string): void {
  try {
    sessionStorage.setItem('last_cta_feature', `profile_photo_${touchpoint}`);
  } catch {
    // sessionStorage not available (private mode edge case)
  }
  pushEvent('profile_photo_nudge_shown', {
    cta_touchpoint: touchpoint,
    cta_location: typeof window !== 'undefined' ? window.location.pathname : '',
  });
}

/**
 * Fired when the user dismisses the nudge without uploading a photo.
 */
export function trackProfilePhotoNudgeDismissed(touchpoint: string): void {
  pushEvent('profile_photo_nudge_dismissed', {
    cta_touchpoint: touchpoint,
  });
}

/**
 * Fired once a user who previously saw a nudge successfully uploads a real
 * profile photo (i.e. `hasCustomProfileImage` flips to true).
 */
export function trackProfilePhotoNudgeCompleted(touchpoint: string): void {
  pushEvent('profile_photo_nudge_completed', {
    cta_touchpoint: touchpoint,
  });
}

// ─── CTA: WhatsAppUpdatesPrompt ──────────────────────────────────────────────

/**
 * Fired when the "get event updates on WhatsApp" prompt becomes visible.
 *
 * @param touchpoint  Where the prompt was shown, e.g. "event_subscribe" |
 *   "event_purchase"
 */
export function trackWhatsAppNudgeShown(touchpoint: string): void {
  pushEvent('whatsapp_nudge_shown', {
    cta_touchpoint: touchpoint,
    cta_location: typeof window !== 'undefined' ? window.location.pathname : '',
  });
}

/** Fired when the user closes the prompt without requesting the opt-in. */
export function trackWhatsAppNudgeDismissed(touchpoint: string): void {
  pushEvent('whatsapp_nudge_dismissed', {
    cta_touchpoint: touchpoint,
  });
}

/**
 * Fired when the opt-in request was accepted by the backend. Note this means
 * PENDING_CONFIRMATION, not consent: the user still has to confirm in WhatsApp.
 */
export function trackWhatsAppNudgeRequested(touchpoint: string): void {
  pushEvent('whatsapp_nudge_requested', {
    cta_touchpoint: touchpoint,
  });
}

// ─── Subscription purchase ───────────────────────────────────────────────────

/**
 * Fired once when a subscription becomes active (detected in SubscriptionActivation).
 * Reads and clears the sessionStorage correlation key so it isn't reused.
 */
export function trackSubscriptionPurchased(planId: string): void {
  let ctaFeatureOrigin: string | null = null;
  try {
    ctaFeatureOrigin = sessionStorage.getItem('last_cta_feature');
    sessionStorage.removeItem('last_cta_feature');
  } catch {
    // ignore
  }
  pushEvent('subscription_purchased', {
    plan_id: planId,
    ...(ctaFeatureOrigin ? { cta_feature_origin: ctaFeatureOrigin } : {}),
  });
}
